import React, { useEffect, useState, useCallback } from "react";
import { Container, Card, CardBody, CardHeader, Row, Col, Table, Button } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const BLUE = "#123a7a";
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const cr = (v) => `₹${(Number(v || 0) / 1e7).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`;
const pct = (v) => `${Number(v || 0).toFixed(2)}%`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");

// Colour ramp for DPD buckets (green → red as delinquency rises).
const BUCKET_COLOR = ["#2dce89", "#8bc34a", "#f6c343", "#fb6340", "#f5365c", "#d6336c", "#a61e4d", "#7a0c33"];

const ParCard = ({ label, value, color, hint }) => (
  <Col md="4" className="mb-4">
    <Card className="shadow border-0" style={{ borderTop: `4px solid ${color}` }}>
      <CardBody>
        <div className="text-uppercase text-muted small font-weight-bold">{label}</div>
        <div className="display-3 font-weight-bold mb-0" style={{ color }}>{pct(value)}</div>
        <div className="text-muted small">{hint}</div>
      </CardBody>
    </Card>
  </Col>
);

const PortfolioReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get(URL.REPORTING_PORTFOLIO);
      setData(res.data || null);
    } catch (e) {
      console.error("Portfolio report failed:", e);
      setError("Could not load the portfolio report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets || [];
  const par = data?.par || {};
  const ticket = data?.ticketSize || [];
  const totalPos = data?.totalPos || 0;

  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC · Reporting</h6>
            <h2 className="text-white mb-0">Portfolio &amp; PAR Report</h2>
          </div>
          <Button color="light" size="sm" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Button>
        </CardBody>
      </Card>

      {error && <Card className="shadow"><CardBody className="text-danger text-center py-4">{error}</CardBody></Card>}

      {!error && (
        <>
          {/* Headline */}
          <Row>
            <Col md="6" className="mb-4">
              <Card className="shadow border-0" style={{ borderTop: `4px solid ${BLUE}` }}>
                <CardBody>
                  <div className="text-uppercase text-muted small font-weight-bold">Total POS (Principal Outstanding)</div>
                  <div className="display-3 font-weight-bold mb-0" style={{ color: BLUE }}>{cr(totalPos)}</div>
                  <div className="text-muted small">{money(totalPos)} · {num(data?.totalLoans)} loans</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* PAR ratios */}
          <Row>
            <ParCard label="PAR &gt; 1 DPD" value={par.par1} color="#f6c343" hint="Any overdue (dpd > 0)" />
            <ParCard label="PAR &gt; 30 DPD" value={par.par30} color="#fb6340" hint="Overdue more than 30 days" />
            <ParCard label="PAR &gt; 90 DPD" value={par.par90} color="#f5365c" hint="NPA — overdue more than 90 days" />
          </Row>

          {/* DPD bucket table */}
          <Card className="shadow mb-4">
            <CardHeader className="border-0"><h3 className="mb-0" style={{ color: BLUE }}>Bucket-wise Portfolio (DPD)</h3></CardHeader>
            <div className="table-scroll">
              <Table className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr><th>DPD Bucket</th><th>POS</th><th>% of Portfolio</th><th>Loans</th><th style={{ width: "30%" }}>Share</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center text-muted py-4">Loading…</td></tr>
                  ) : buckets.length === 0 ? (
                    <tr><td colSpan="5" className="text-center text-muted py-4">No portfolio data.</td></tr>
                  ) : buckets.map((b, i) => (
                    <tr key={b.label}>
                      <td>
                        <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: BUCKET_COLOR[b.key] || "#8898aa", marginRight: 8 }} />
                        {b.label}
                      </td>
                      <td className="font-weight-bold">{money(b.pos)}</td>
                      <td>{pct(b.pct)}</td>
                      <td>{num(b.loans)}</td>
                      <td>
                        <div style={{ background: "#eef1f6", borderRadius: 4, height: 10, overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(b.pct, 100)}%`, height: "100%", background: BUCKET_COLOR[b.key] || "#8898aa" }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {!loading && buckets.length > 0 && (
                  <tfoot>
                    <tr className="font-weight-bold" style={{ background: "#f6f9fc" }}>
                      <td>Total POS</td><td>{money(totalPos)}</td><td>100.00%</td><td>{num(data?.totalLoans)}</td><td></td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </Card>

          {/* Ticket-size cut */}
          <Card className="shadow mb-5">
            <CardHeader className="border-0"><h3 className="mb-0" style={{ color: BLUE }}>Ticket-size wise Portfolio</h3></CardHeader>
            <div className="table-scroll">
              <Table className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr><th>Ticket Size</th><th>POS</th><th>% of Portfolio</th><th>Loans</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4" className="text-center text-muted py-4">Loading…</td></tr>
                  ) : ticket.length === 0 ? (
                    <tr><td colSpan="4" className="text-center text-muted py-4">No data.</td></tr>
                  ) : ticket.map((b) => (
                    <tr key={b.label}>
                      <td>{b.label}</td>
                      <td className="font-weight-bold">{money(b.pos)}</td>
                      <td>{pct(b.pct)}</td>
                      <td>{num(b.loans)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </Container>
  );
};

export default PortfolioReport;
